import type { FilterRule, MobileCardConfiguration } from '../../types/list';

/**
 * Shared constants and filter rules for invoice lists
 */

/**
 * Base required fields for all invoice lists
 */
export const BASE_REQUIRED_FIELDS = [
	'_id',
	'externalId',
	'externalId2',
	'invoiceNumber',
	'date',
	'dueDate',
	'_fk_client__id',
	'fk_company__id',
	'clientName',
	'clientExternalId',
	'clientExternalId2',
	'clientAddress1',
	'clientAddress2',
	'clientCity',
	'clientState',
	'clientZip',
	'total',
	'companyName',
];

/**
 * Extended required fields for the "All" list (includes all column data)
 */
export const ALL_REQUIRED_FIELDS = [
	...BASE_REQUIRED_FIELDS,
	'numberOfLines',
	'tax',
	'freight',
	'discount',
	'subtotal',
	'salesRepFirstName',
	'm_sm_payment_outstandingBalance',
	'paymentStatus',
	'totalGP',
];

/**
 * Required fields for aging bucket lists (outstanding balance + payment status needed)
 */
export const AGING_REQUIRED_FIELDS = [
	...BASE_REQUIRED_FIELDS,
	'm_sm_payment_outstandingBalance',
	'paymentStatus',
];

/**
 * Base filter rules applied to all invoice lists.
 *
 * PD-16348: a user without invoices:view:any opens on their own clients'
 * invoices ("Me") and is bounded to them — plus "Unassigned", the invoices of
 * clients with no salesperson, for a holder of clients:view:unassigned. That
 * choice is an opt-in (`applyAsDefault: false`): mixed into a rep's everyday
 * list, unassigned accounts' invoices would read as clutter. It makes the list
 * agree with opening an invoice by address, which already allows an
 * unassigned client's invoice to that holder. The server applies the same
 * rule, and a request that names no salesperson gets the default, Me.
 */
export const BASE_FILTER_RULES: Record<string, FilterRule> = {
	invoiceSalesRep: {
		defaultValue: [{ id: 'ME', label: 'Me' }],
		additionalValues: [
			{
				values: [{ id: 'null', label: 'Unassigned' }],
				applyIf: {
					type: 'group',
					joiner: 'AND',
					negated: false,
					children: [
						{ type: 'rule', action: 'clients:view:unassigned', negated: false },
					],
				},
				applyAsDefault: false,
			},
		],
		applyDefaultIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [{ type: 'rule', action: 'invoices:view:any', negated: true }],
		},
		boundedIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [{ type: 'rule', action: 'invoices:view:any', negated: true }],
		},
		// Nothing to choose without the Unassigned opt-in, so the filter stays
		// out of sight for those users, as it always was.
		hiddenIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [
				{ type: 'rule', action: 'invoices:view:any', negated: true },
				{ type: 'rule', action: 'clients:view:unassigned', negated: true },
			],
		},
	},
	invoiceDateRange: {
		defaultValue: [
			{
				id: 'last-90-days',
				label: 'Last 90 Days',
				data: {
					startDate: '{{DATE:-90d}}',
					endDate: '{{DATE:0d}}',
				},
			},
		],
	},
};

/**
 * PD-13292: mobile card layout for the invoice lists, on the zones engine.
 * The Total renders as the hero metric in the card header (the figure the
 * list is scanned for), Amount Owed / Date / Sales Rep / Lines flow as
 * inline meta chips, the client block renders full-width, and the payment
 * badge sits in the footer. Subtotal, adjustments (tax/freight/discount)
 * and GP stay desktop-only — on a phone they're noise next to Total +
 * Amount Owed.
 */
export const INVOICE_MOBILE_CARD_CONFIG: MobileCardConfiguration = {
	cardLayout: 'zones',
	primaryFields: ['invoice-number'],
	heroField: 'invoice-totals.total-field',
	wideFields: ['client-info'],
	hiddenFields: ['invoice-totals.subtotal-field', 'adjustments', 'gp-totals'],
	// "Sales Rep" eats half the meta column's width before the name even
	// starts; "Rep" keeps most names on one line (review note).
	labelOverrides: { salesperson: 'Rep' },
};
